import { type AnyAction, type Middleware } from 'redux';
import basketRewardsActions from '@/redux/basketRewards/actions';
import conveyanceModeActions from '@/redux/conveyanceMode/actions';
import orderStatusActions from '@/redux/orderStatus/actions';
import { isBasketMutating } from '@/redux/orderStatus/reducer';
import { type RootState } from '@/types/app';
import { OrderStatusTypes } from '@/types/orderStatus';
import { sentryLogger } from '@/utils/sentryLogger';

// Bounded escape: if state still reports "mutating" this many ms after the
// mutation began, presume the response was lost and release a deferred write
// anyway so a hung request cannot permanently brick checkout.
export const STUCK_MUTATION_TIMEOUT_MS = 30_000;

// Actions that trigger a basket write. Dispatching any of these while another
// basket-writing action is still in flight would race the in-flight PATCH
// (TEAM-14696), so the middleware *defers* the newcomer — it is held out of the
// reducer and the saga channel until the in-flight mutation settles, then
// replayed. Runs before the reducer, so `isBasketMutating` reflects pre-dispatch
// state and the current action's own flip cannot self-blind the check.
//
// basketRewards/{ADD,REMOVE}_REWARD and conveyanceMode/CONVEYANCE_MODE_SET are
// saga triggers whose worker then dispatches the corresponding orderStatus status
// action. We guard the trigger (not the inner status action) so the API call is
// skipped, not just the reducer flip.
const GUARDED_ACTION_TYPES: ReadonlySet<string> = new Set([
  orderStatusActions.VALIDATE_ORDER,
  orderStatusActions.TIP_ADD,
  orderStatusActions.UTENSILS_SET,
  orderStatusActions.BASKET_INSTRUCTIONS_SET,
  orderStatusActions.PROMO_CODE_ADD,
  orderStatusActions.PROMO_CODE_REMOVE,
  orderStatusActions.WANTED_AT_BASKET_SET,
  orderStatusActions.WANTED_AT_BASKET_REMOVE,
  basketRewardsActions.ADD_REWARD,
  basketRewardsActions.REMOVE_REWARD,
  // The two conveyance write triggers. TEAM-14698 gave each saga an ad-hoc guard
  // against *another conveyance* being in flight, which does not cover a tip or
  // validate — and "conveyance flips to delivery while a validate is in flight"
  // is the second trigger TEAM-14696 names. The checkout handoff toggle
  // (features/checkout/handoff/mode-toggle.tsx) and the delivery-unavailable
  // switch-to-pickup both dispatch CONVEYANCE_MODE_SET with no gating at all.
  conveyanceModeActions.CONVEYANCE_MODE_SET,
  orderStatusActions.CONVEYANCE_DELIVERY_SET,
]);

// The basket is no longer the diner's to edit: submit has fired, or the order
// state was torn down. Anything still deferred is abandoned rather than replayed
// — writing a tip onto a basket the diner has already paid for is worse than
// losing it.
//
// The submission triggers are in here, not just DESTROY_ORDER, because the
// success path is not the only way out of a submit. `submitOrderSaga` returns
// early on `OrderStatus.IN_PROGRESS` without ever reaching `destroyOrder()`, so a
// queue keyed only on teardown would survive, and `scheduleStuckRelease` would
// force-replay a basket write 30s later against an order already at the POS. The
// TRANSMITTING modal hides the controls, but it does not empty the queue.
// Clearing on the trigger is safe in the other direction too: if submission
// fails, `type` returns to ERROR/OK and the diner can redo the edit deliberately.
const SESSION_END_ACTION_TYPES: ReadonlySet<string> = new Set([
  orderStatusActions.DESTROY_ORDER,
  orderStatusActions.ORDER_NOT_INITIALIZED,
  orderStatusActions.ORDER_SUBMISSION_TRIGGER,
  orderStatusActions.ORDER_SUBMISSION_DIGITAL_WALLET_TRIGGER,
  orderStatusActions.DIGITAL_WALLET_ORDER_STARTED,
]);

/**
 * TEAM-15341: a 502 recovery modal is open. Not a basket write, but a deferred write must not
 * be replayed — and a new one must not pass — while the diner is reading it: the write's own
 * status actions (e.g. CONVEYANCE_DELIVERY_SETTING, then CONVEYANCE_SET / orderPending) replace
 * RECOVERY_502 and the modal disappears mid-read. Reproduced in the AC4 browser run, where a
 * delivery write queued during the submit replayed ~40ms after the conveyance modal opened.
 * Writes wait until the modal is dismissed (FORCE_ORDER_PENDING moves the type off
 * RECOVERY_502), then drain as usual. The stuck-mutation timer is NOT involved: an open modal
 * is not an in-flight mutation, so nothing here is force-released after 30s.
 */
const isRecoveryModalOpen = (state: RootState): boolean =>
  state.app.orderStatus.type === OrderStatusTypes.RECOVERY_502;

// Kill switch. This middleware sits in front of every basket write and no
// environment exercises coo-api before production, so there needs to be a way to
// take it out without shipping a revert. Read off `process.env` directly rather
// than through `constants/envConfig` so it stays optional — that module's zod
// schema makes every key it lists a required build variable. Next inlines
// `NEXT_PUBLIC_*` at build time, so flipping it needs a redeploy, not a restart.
//
// Deliberately opt-out: anything other than the exact string "true" leaves the
// guard on, so a typo or an unset variable fails safe.
const guardDisabled = process.env.NEXT_PUBLIC_DISABLE_BASKET_MUTATION_GUARD === 'true';

/** Pass-through used when the guard is switched off. */
const passThroughMiddleware: Middleware<{}, RootState> = () => (next) => (action) => next(action);

// Factory (not a singleton) so each SSR-created store gets its own queue/timer.
export const createBasketMutationGuard = (): Middleware<{}, RootState> => {
  if (guardDisabled) {
    return passThroughMiddleware;
  }

  /**
   * Basket writes held back while another one is in flight, keyed by action type.
   *
   * Deferring rather than dropping is the whole point: a dropped write leaves the
   * UI showing a choice the server never received, which is the displayed-total vs.
   * stored-total divergence TEAM-14696 exists to remove. Keying by type collapses a
   * burst on one control (two tip taps) down to the diner's latest choice, and
   * re-deferring deletes before inserting so the entry moves to the tail — order
   * between *different* controls is what decides the final basket state
   * (`wantedAtBasketSet` then `wantedAtBasketRemove` must not replay reversed).
   */
  const deferred = new Map<string, AnyAction>();
  let mutationStartedAt: number | null = null;
  let stuckReleaseTimer: ReturnType<typeof setTimeout> | null = null;
  let replaying = false;
  // Set for exactly one re-entrant dispatch when the stuck-mutation timer gives
  // up on the in-flight write. Without it the released action would hit the
  // guard again — state still reports mutating — and be re-deferred forever.
  let releasingStuckWrite = false;

  return (store) => {
    const clearStuckReleaseTimer = () => {
      if (stuckReleaseTimer !== null) {
        clearTimeout(stuckReleaseTimer);
        stuckReleaseTimer = null;
      }
    };

    /**
     * Replays the oldest deferred write. Re-enters this middleware, so it is
     * re-guarded unless `force` says the stuck timer is overriding the guard.
     */
    const replayOldest = ({ force = false }: { force?: boolean } = {}) => {
      const oldest = deferred.entries().next();
      if (oldest.done) {
        return;
      }
      const [actionType, deferredAction] = oldest.value;
      deferred.delete(actionType);
      releasingStuckWrite = force;
      try {
        store.dispatch(deferredAction);
      } finally {
        releasingStuckWrite = false;
      }
    };

    /**
     * Drains the queue one write at a time. Each replay flips `isBasketMutating`
     * back on, which stops the loop; the next release schedules the following
     * drain. `deferred.size + 1` bounds the loop against a replay that immediately
     * re-defers itself.
     */
    const drainWhileIdle = () => {
      if (replaying) {
        return;
      }
      replaying = true;
      try {
        let budget = deferred.size + 1;
        while (
          deferred.size > 0 &&
          budget-- > 0 &&
          !isBasketMutating(store.getState()) &&
          !isRecoveryModalOpen(store.getState())
        ) {
          replayOldest();
        }
      } finally {
        replaying = false;
      }
    };

    /**
     * Releases one deferred write once the in-flight mutation has outlived
     * `STUCK_MUTATION_TIMEOUT_MS` without ever reporting idle — a lost response
     * must not strand the queue forever.
     */
    const scheduleStuckRelease = () => {
      if (stuckReleaseTimer !== null || deferred.size === 0) {
        return;
      }
      const elapsed = mutationStartedAt === null ? 0 : Date.now() - mutationStartedAt;
      stuckReleaseTimer = setTimeout(
        () => {
          stuckReleaseTimer = null;
          if (deferred.size === 0 || !isBasketMutating(store.getState())) {
            return;
          }
          sentryLogger.warn(
            'basketMutationGuard: releasing a deferred write after stuck mutation',
            {
              actionType: deferred.keys().next().value,
              deferredCount: deferred.size,
              elapsedMs: mutationStartedAt === null ? null : Date.now() - mutationStartedAt,
            },
          );
          // Reset the clock so a still-hung backend releases at most one write
          // per window rather than opening the floodgates.
          mutationStartedAt = Date.now();
          replayOldest({ force: true });
          scheduleStuckRelease();
        },
        Math.max(STUCK_MUTATION_TIMEOUT_MS - elapsed, 0),
      );
      // No-op in browsers; keeps a short-lived SSR store from holding the event loop.
      (stuckReleaseTimer as unknown as { unref?: () => void })?.unref?.();
    };

    return (next) => (action) => {
      if (SESSION_END_ACTION_TYPES.has(action.type)) {
        deferred.clear();
        clearStuckReleaseTimer();
      }

      // `bypassBasketMutationGuard` is set only by initializeOrderSaga so it can
      // reuse promoCodeAddSaga during init while status is INITIALIZING_ORDER.
      // Deferral cannot serve that call site: it blocks on
      // `take(PROMO_CODE_ADD)`, and the mutation whose completion would release
      // the queue is its own — deferring there deadlocks init.
      // See orderStatus/saga.ts near the promo block.
      if (
        GUARDED_ACTION_TYPES.has(action.type) &&
        !action.bypassBasketMutationGuard &&
        !releasingStuckWrite
      ) {
        if (isBasketMutating(store.getState())) {
          const elapsed = mutationStartedAt === null ? 0 : Date.now() - mutationStartedAt;
          if (elapsed <= STUCK_MUTATION_TIMEOUT_MS) {
            // Delete before set so a re-deferred write moves to the tail.
            deferred.delete(action.type);
            deferred.set(action.type, action);
            scheduleStuckRelease();
            // Returning the action keeps redux's `dispatch` contract. The write
            // is queued, not discarded, so a call site that latches a ref on the
            // next line is not recording something that will never happen.
            return action;
          }
          sentryLogger.warn(
            'basketMutationGuard: forcing guarded action through after stuck mutation',
            {
              actionType: action.type,
              elapsedMs: elapsed,
            },
          );
          // Reset the escape timer so a still-hung backend doesn't allow
          // unbounded pass-through on every subsequent dispatch.
          mutationStartedAt = Date.now();
        } else if (isRecoveryModalOpen(store.getState())) {
          // Held, not dropped, for the same reason as a deferral above; replayed once the
          // modal is dismissed. No stuck timer: nothing is in flight.
          deferred.delete(action.type);
          deferred.set(action.type, action);
          return action;
        }
      }

      const result = next(action);

      // Track transitions in `isBasketMutating` so the escape timer covers
      // non-guarded triggers too (e.g. INITIALIZE_ORDER, submission actions).
      if (isBasketMutating(store.getState())) {
        if (mutationStartedAt === null) {
          mutationStartedAt = Date.now();
        }
        scheduleStuckRelease();
      } else {
        mutationStartedAt = null;
        clearStuckReleaseTimer();
        if (deferred.size > 0 && !replaying && !isRecoveryModalOpen(store.getState())) {
          // Drain on a microtask, not inline: the action that releases the guard
          // is usually an `orderPending()` put from inside a saga, and replaying
          // synchronously would land the deferred write in the middle of that
          // saga's remaining statements.
          void Promise.resolve().then(drainWhileIdle);
        }
      }

      return result;
    };
  };
};
